import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { products } from '../../data/products';
import ProductImageGallery from '../../components/ProductImageGallery';
import RelatedProducts from '../../components/RelatedProducts';
import { useCart } from '../../context/CartContext';

const AromatherapyDiffuser = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === 18) || products[17]; // Aromatherapy Diffuser
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedEssentialOil, setSelectedEssentialOil] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState('/images/shop-products/aromatherapy-diffuser/1.jpeg');

  const images = [
    "/images/shop-products/aromatherapy-diffuser/1.jpeg",
    "/images/shop-products/aromatherapy-diffuser/2.jpg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_14f6f95b_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_559608f4_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_55e987fb_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_68109eb1_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_803c782f_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/sg_brand_250ml_newest_rgb_arom_1705475605_c6e8aee1_progressive.jpeg",
    "/images/shop-products/aromatherapy-diffuser/y.jpeg",
    "/images/shop-products/aromatherapy-diffuser/z.jpeg"
  ];

  const colors = [
    { name: 'Black', image: '/images/shop-products/aromatherapy-diffuser/black.jpg' },
    { name: 'White', image: '/images/shop-products/aromatherapy-diffuser/white.png' }
  ];
  const essentialOils = [
    { name: 'Lemon', price: 2.90, image: '/images/shop-products/essential-oil-set/lemon.jpg' },
    { name: 'Lily', price: 2.90, image: '/images/shop-products/essential-oil-set/lily.jpg' },
    { name: 'Rose', price: 2.90, image: '/images/shop-products/essential-oil-set/rose.jpg' },
    { name: 'White Tea', price: 2.90, image: '/images/shop-products/essential-oil-set/white tea.jpg' },
    { name: 'Lavender', price: 2.90, image: '/images/shop-products/essential-oil-set/lavender.jpg' },
    { name: 'Jasmine', price: 2.90, image: '/images/shop-products/essential-oil-set/Jasmine.jpg' }
  ];

  // 获取当前选择的图片（用于缩略图显示）
  const getSelectedImage = () => {
    // 只有在用户主动选择了精油时才显示精油图片
    if (selectedEssentialOil) {
      const selectedOilData = essentialOils.find(oil => oil.name === selectedEssentialOil);
      return selectedOilData ? selectedOilData.image : null;
    }
    // 只有在用户主动选择了不同颜色时才显示颜色图片
    // 默认的 'Black' 不显示，因为主图已经是黑色产品
    if (selectedColor && selectedColor !== 'Black') {
      const selectedColorData = colors.find(c => c.name === selectedColor);
      return selectedColorData ? selectedColorData.image : null;
    }
    return null;
  };

  // 构建图片数组，包含主图和选择图片
  const allImages = [
    mainImage, // 主图始终是第一个
    ...images, // 其他产品图片
    ...(getSelectedImage() ? [getSelectedImage()] : []) // 如果有选择，添加到末尾
  ];

  // 主图始终保持首图，不根据选择自动更新

  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'Home & Lifestyle', href: '/category/home-lifestyle' },
    { name: product.name, href: '#' }
  ];

  const relatedProducts = products
    .filter(p => p.category === 'home-lifestyle' && p.id !== product.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Breadcrumbs */}
      <nav className="bg-gray-800 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-sm text-gray-300">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.name}>
                {index > 0 && <span>/</span>}
                <Link
                  to={crumb.href}
                  className="hover:text-white transition-colors"
                >
                  {crumb.name}
                </Link>
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <ProductImageGallery
              images={allImages}
              currentImage={mainImage}
              onImageChange={(image) => {
                setMainImage(image);
              }}
            />
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="ml-2 text-gray-300">{product.rating}</span>
                </div>
                <span className="text-gray-400">({product.reviews} reviews)</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-white">{product.price}</span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-400 line-through">{product.originalPrice}</span>
                )}
                {product.originalPrice && (
                  <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-medium">
                    {Math.round(((parseFloat(product.originalPrice.replace(/^S\$/, '')) - parseFloat(product.price.replace(/^S\$/, ''))) / parseFloat(product.originalPrice.replace(/^S\$/, ''))) * 100)}% OFF
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <span className={`w-3 h-3 rounded-full ${product.inStock ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className={product.inStock ? 'text-green-400' : 'text-red-400'}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>

              <p className="text-gray-300 text-lg leading-relaxed">{product.description}</p>
            </div>

            {/* Product Options */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-3">Choose Colour</h3>
                <div className="flex gap-4 flex-wrap">
                  {colors.map((color) => (
                    <div key={color.name} className="flex flex-col">
                      <button 
                        className={`w-[100px] h-[100px] border ${color.name === selectedColor ? 'border-2 border-green-500' : 'border border-gray-600'} 
                          rounded-md bg-gray-800 flex items-center justify-center overflow-hidden relative hover:border-green-400 transition-colors aspect-square`}
                        onClick={() => setSelectedColor(color.name)}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <img 
                            src={color.image}
                            alt={color.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/images/products/placeholder.jpg';
                            }}
                          />
                        </div>
                        {color.name === selectedColor && (
                          <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-r-[20px] border-t-transparent border-r-green-500"></div>
                        )}
                      </button>
                      <div className="mt-2 text-left max-w-[100px]">
                        <div className="text-sm text-white font-medium leading-tight">{color.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Add on</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {essentialOils.map((oil) => (
                    <button 
                      key={oil.name}
                      className={`w-full border rounded-lg p-4 transition-all duration-200 ${
                        selectedEssentialOil === oil.name 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-gray-700 hover:border-green-400'
                      }`}
                      onClick={() => setSelectedEssentialOil(selectedEssentialOil === oil.name ? null : oil.name)}
                    >
                      <div className="flex items-center space-x-4">
                        <img 
                          src={oil.image}
                          alt={oil.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.src = '/images/products/placeholder.jpg';
                          }}
                        />
                        <div className="flex-1 text-left">
                          <div className="text-white font-medium text-base mb-1">{oil.name}</div>
                          <div className="text-gray-400 text-sm mb-2">Essential oil for aromatherapy (10ml)</div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-500 font-semibold text-lg">+S${oil.price}</div>
                          {selectedEssentialOil === oil.name && (
                            <div className="text-green-500 text-sm mt-1">✓ Selected</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-400 transition-colors"
                  >
                    -
                  </button>
                  <span className="w-16 text-center text-lg font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-400 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button 
                className={`w-full font-bold py-4 px-6 rounded-lg transition-colors ${
                  selectedColor 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (!selectedColor) return;
                  
                  const totalPrice = parseFloat(product.price.replace(/^S\$/, '')) * quantity + (selectedEssentialOil ? 2.90 : 0);
                  addToCart({
                    id: product.id,
                    name: product.name,
                    price: `S$${totalPrice.toFixed(2)}`,
                    image: product.image,
                    quantity: quantity,
                    color: selectedColor,
                    selectedEssentialOil: selectedEssentialOil
                  });
                }}
                disabled={!selectedColor}
              >
                {selectedColor 
                  ? `Add to Cart - S$${(parseFloat(product.price.replace(/^S\$/, '')) * quantity + (selectedEssentialOil ? 2.90 : 0)).toFixed(2)}`
                  : 'Please Select Colour'
                }
              </button>
              <button 
                className={`w-full font-bold py-4 px-6 rounded-lg transition-colors ${
                  selectedColor 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (!selectedColor) return;
                  
                  const totalPrice = parseFloat(product.price.replace(/^S\$/, '')) * quantity + (selectedEssentialOil ? 2.90 : 0);
                  addToCart({
                    id: product.id,
                    name: product.name,
                    price: `S$${totalPrice.toFixed(2)}`,
                    image: product.image,
                    quantity: quantity,
                    color: selectedColor,
                    selectedEssentialOil: selectedEssentialOil
                  });
                  navigate('/cart');
                }}
                disabled={!selectedColor}
              >
                {selectedColor ? 'Buy Now' : 'Please Select Colour'}
              </button>
            </div>

            {/* Shipping Info */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                 <div>
                   <p className="font-medium text-white">Free Meet Up (Available at Sengkang & Orchard locations)</p>
                   <p className="text-sm text-gray-400">Available Shipping</p>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mt-16">
          <div className="border-b border-gray-700 mb-8">
            <div className="flex space-x-8">
              <button 
                onClick={() => setActiveTab('overview')}
                className={`pb-4 px-2 font-medium ${activeTab === 'overview' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
              >
                Overview
              </button>
              <button 
                onClick={() => setActiveTab('specifications')}
                className={`pb-4 px-2 font-medium ${activeTab === 'specifications' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
              >
                Specifications
              </button>
              <button 
                onClick={() => setActiveTab('reviews')}
                className={`pb-4 px-2 font-medium ${activeTab === 'reviews' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
              >
                Reviews ({product.reviews})
              </button>
            </div>
          </div>

          <div className="py-8">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Product Overview */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Product Overview</h3>
                  <div className="space-y-4 text-white">
                    <p>
                      Experience the magic of aromatherapy with our 7 Color Flame Aromatherapy Diffuser. 
                      This innovative device combines the soothing benefits of essential oils with a mesmerizing 
                      flame effect that creates a warm, cozy atmosphere in any room.
                    </p>
                    <p>
                      The ultrasonic technology breaks down water and essential oils into ultra-fine mist particles, 
                      ensuring even distribution of fragrance throughout your space. Choose from 7 different flame colors 
                      to match your mood and create the perfect ambiance for relaxation, meditation, or sleep.
                    </p>
                    <p>
                      Perfect for bedrooms, living rooms, offices, or any space where you want to create a calming environment. 
                      The silent operation won't disturb your peace, while the auto shut-off feature ensures safety when the water tank is empty.
                    </p>
                  </div>
                </div>

                {/* Key Features */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Key Features</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">7 Color Flame Effect</div>
                        <div className="text-gray-300 text-sm">Realistic flame simulation with 7 different colors to create a cozy and relaxing atmosphere</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Ultrasonic Technology</div>
                        <div className="text-gray-300 text-sm">High-frequency ultrasonic waves create ultra-fine mist particles for better aroma diffusion</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Silent Operation</div>
                        <div className="text-gray-300 text-sm">Quiet DC motor ensures peaceful sleep and relaxation without disturbing noise</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <div className="text-white font-medium mb-1">Auto Shut-off</div>
                        <div className="text-gray-300 text-sm">Automatically turns off when water tank is empty to protect the device and ensure safety</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Technical Specifications */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Technical Specifications</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Product Model</span>
                      <span className="text-white font-medium">DQ701</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Water Capacity</span>
                      <span className="text-white font-medium">180ml</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Rated Input</span>
                      <span className="text-white font-medium">DC5.0V/2.0A</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Working Current</span>
                      <span className="text-white font-medium">≤900mA</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Rated Power</span>
                      <span className="text-white font-medium">≤4.5W</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Spray Volume</span>
                      <span className="text-white font-medium">15-20ml/h</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Product Material</span>
                      <span className="text-white font-medium">ABS/PP/Electronic</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Product Size</span>
                      <span className="text-white font-medium">171×75×101mm</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Coverage Area</span>
                      <span className="text-white font-medium">60 m²</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Humidification Duration</span>
                      <span className="text-white font-medium">6-9 Hours</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-700">
                      <span className="text-gray-400">Flame Colors</span>
                      <span className="text-white font-medium">7 Colors</span>
                    </div>
                    <div className="flex justify-between py-3">
                      <span className="text-gray-400">Executive Standard</span>
                      <span className="text-white font-medium">GB4706.1-2005</span>
                    </div>
                  </div>
                </div>

                {/* Features & Benefits */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Features & Benefits</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">7 Color Flame Effect</h4>
                        <span className="text-red-400 text-sm">Visual Appeal</span>
                      </div>
                      <p className="text-gray-300 text-sm">Realistic flame simulation with 7 different colors to create a cozy and relaxing atmosphere</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Ultrasonic Technology</h4>
                        <span className="text-blue-400 text-sm">Advanced</span>
                      </div>
                      <p className="text-gray-300 text-sm">High-frequency ultrasonic waves create ultra-fine mist particles for better aroma diffusion</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Silent Operation</h4>
                        <span className="text-green-400 text-sm">Quiet</span>
                      </div>
                      <p className="text-gray-300 text-sm">Quiet DC motor ensures peaceful sleep and relaxation without disturbing noise</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Auto Shut-off</h4>
                        <span className="text-yellow-400 text-sm">Safety</span>
                      </div>
                      <p className="text-gray-300 text-sm">Automatically turns off when water tank is empty to protect the device and ensure safety</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Large Coverage</h4>
                        <span className="text-purple-400 text-sm">Efficient</span>
                      </div>
                      <p className="text-gray-300 text-sm">Covers up to 60m² area with continuous 6-9 hours of operation</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Easy to Use</h4>
                        <span className="text-orange-400 text-sm">Convenient</span>
                      </div>
                      <p className="text-gray-300 text-sm">Simple one-button operation with LED indicators for easy control</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">Customer Reviews</h3>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                    Write a Review
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gray-800 p-6 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">MJ</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">Maria Johnson</p>
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">2 days ago</span>
                    </div>
                    <p className="text-gray-300">
                      This aromatherapy diffuser is absolutely amazing! The flame effect is so realistic and calming, 
                      and the 7 different colors create the perfect ambiance. The ultrasonic technology ensures 
                      even distribution of fragrance throughout the room. The silent operation is perfect for 
                      bedtime use, and the auto shut-off feature gives me peace of mind. Highly recommended!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white mb-8">Related Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts.map((relatedProduct) => (
              <Link
                key={relatedProduct.id}
                to={`/product/${relatedProduct.id}`}
                className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors group"
              >
                <div className="aspect-square bg-gray-700 overflow-hidden">
                  <img
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-white group-hover:text-blue-400 transition-colors">
                    {relatedProduct.name}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">${relatedProduct.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      {/* 相关产品 */}
      <RelatedProducts 
        currentProductId={18} 
        currentCategory="home-decor" 
        limit={4} 
      />
    </div>
  );
};

export default AromatherapyDiffuser;
